import { generateKeyBetween } from 'fractional-indexing';
import * as Y from 'yjs';
import { reorderNode } from '../action/layer';
import { removeHideProperties } from '../lib/clipboard';
import { Rect, Region } from '../lib/region';
import { createId, sortByIndex } from '../render/utils/node';
import { FRAME_LAYOUT_MODES, TYPES } from '../render/constants';
import { getNodeMatrix } from '../render/effects/matrix';
import { layoutTextNode, wrapText } from '../render/text';
import { diffText, updateHighlightByText } from '../render/utils/text';
import type { RenderContext } from '../render/context/createRenderContext';
import type { NodeLike, NodeType } from '../render/context/node';
import { signal } from '../common-utils/signal';

export interface StateAPI {
  transact: (fn: () => void) => void;
  undo: () => void;
  redo: () => void;
  getNextIndex: (index: string | null) => string;
  generateKeyBetween: (index1: string | null, index2: string | null) => string;
  createId: () => string;
  addNode: (
    node: Partial<NodeLike> & { type: string },
    options?: { useInputId?: boolean }
  ) => NodeType;
  shouldSkip: (node: NodeLike) => boolean;
  reorderNode: (id: string, type: string) => void;
  getNode: (id: string) => NodeType | null;
  getNodes: () => NodeType[];
  getRootNodes: () => NodeType[];
  removeNode: (id: string, force?: boolean) => void;
  getAncenstors: (id: string) => NodeType[];
  getFrameOf: (id: string) => NodeType | null;
  getChildren: (id: string | null) => NodeType[];
  getDescendants: (id: string) => NodeType[];
  updateNodeText: (id: string, patch: { text: string }) => void;
  updateNode: (id: string, patch: Partial<NodeType>, options?: { force?: boolean }) => void;
  markNodeUpdate: (node: NodeType, changedKeys?: string[]) => void;
  markNeedRender: () => void;
  isNodeLocked: (node: NodeType) => boolean;
  getNodeBox: (node?: NodeType | null) => Rect | null;
  getNodeRegion: (node: NodeType) => Region | null;
  getSelectedNodesRegion: () => Rect & { nodes: NodeLike[] };
  getNodesRegion: (nodes: NodeType[]) => Rect;
  addFrame: (node: Partial<NodeLike>) => NodeLike;
  getFrames: () => NodeLike[];
  layoutFrame: () => void;
  setLayoutMode: (mode: string) => void;
  setClientConfig: (key: string, value: any) => void;
  getClientConfig: (key: string, defaultValue?: any) => any;
  getAllClientConfig: () => Record<string, any>;
  readClientConfig: () => void;
  clearClientConfig: () => void;
  getNodeName: (name?: string, nodes?: NodeLike[]) => string;
  export: () => { nodes: Record<string, NodeLike> };
  import: (state: { nodes: Record<string, NodeLike> }) => void;
  clearUndoStack: () => void;
}

export class StateProvider {
  ydoc!: Y.Doc;
  stageContext!: RenderContext;
  undoManager!: Y.UndoManager;
  disposed: boolean;
  state: {
    nodes: Record<string, NodeLike>;
  };
  cache: {
    _nodes?: NodeType[] | null;
    _descendants?: Map<string, NodeType[]> | null;
  };

  // FIXME: should provide more generic way to watch node changes
  frameChange$ = signal(0);
  nodeMoved$ = signal<number | NodeType>(0);
  // FIXME: this property is not reliable if user execute the undo/redo operation
  lastUpdatedNode$ = signal<NodeType | null>(null);

  constructor() {
    this.state = {
      nodes: {},
    };
    this.cache = {};
    this.disposed = false;
  }

  attach(stageContext: RenderContext) {
    this.stageContext = stageContext;
    this.setState(null);
    const api: StateAPI = {
      transact: (fn) => this.ydoc.transact(fn),
      undo: () => {
        if (this.undoManager) {
          // 添加空值检查
          this.undoManager.undo();
          this.nodeMoved$.set(Math.random());
          stageContext.hooks.onLayerChange?.();
        }
      },
      redo: () => {
        if (this.undoManager) {
          // 添加空值检查
          this.undoManager.redo();
          this.nodeMoved$.set(Math.random());
          stageContext.hooks.onLayerChange?.();
        }
      },
      clearUndoStack: () => {
        if (this.undoManager) {
          stageContext.hooks.onLayerChange?.();
          this.undoManager.clear();
        }
      },
      getNextIndex: (index) => generateKeyBetween(index, null),
      generateKeyBetween: generateKeyBetween,
      createId: () => createId(),
      addNode: (node, { useInputId = false } = {}) => {
        if (!TYPES[node.type]) {
          throw new Error(
            `Invalid node type: ${node.type}. Valid types are: ${Object.keys(TYPES).join(', ')}`
          );
        }

        if (!useInputId) node.id = createId();
        const parentId = node.parentId || null;
        const parentNode = parentId ? api.getNode(parentId) : null;
        let siblings: NodeLike[] = [];
        if (node.type === TYPES.frame) {
          siblings = stageContext.api.getFrames();
        } else {
          siblings = stageContext.api.getChildren(parentId);
        }
        const lastNode = siblings[siblings.length - 1] || parentNode;

        let typeName = `${node.type}`;
        typeName = typeName.charAt(0).toUpperCase() + typeName.slice(1);
        const name = node.name || typeName;
        const newName = api.getNodeName(name || '', stageContext.api.getNodes());

        const newNode = removeHideProperties({
          rotation: 0,
          x: 0,
          y: 0,
          w: 100,
          h: 100,
          ...node,
          name: newName,
          index: generateKeyBetween(lastNode?.index ?? null, null),
        });

        this.ydoc.transact(() => {
          const nodes = this.ydoc.getMap('board').get('nodes');
          createSyncNode(newNode, nodes);
        });

        this.cache._nodes = null;
        this.cache._descendants = null; // 清除descendants缓存
        const finalNode = api.getNode(newNode.id)!;
        api.markNodeUpdate(finalNode);
        stageContext.hooks.onLayerChange?.();
        stageContext.api.loadFontsInUsed?.();
        return finalNode;
      },

      shouldSkip: (node) => {
        if (!node) return true; // 添加空值检查
        if (node.skipInteractive) return true;
        if (node.locked) return true;
        return false;
      },
      reorderNode: (id, type) => reorderNode(stageContext, id, type),
      getNode: (id) => this.state?.nodes[id] as NodeType | null,
      getNodes: () => {
        if (!this.state) return [];
        if (this.cache?._nodes) return this.cache._nodes;
        const nodes = Object.values(this.state.nodes) as NodeType[];
        const list = nodes.sort(sortByIndex);
        if (this.cache) this.cache._nodes = list;
        return list;
      },
      getRootNodes: () => {
        return stageContext.api.getNodes().filter((n) => !n.parentId);
      },
      removeNode: (id, force) => {
        const ydoc = this.ydoc;
        const node = api.getNode(id);
        if (!node) return; // 添加空值检查
        if (!force && api.isNodeLocked(node)) return;
        if (!force && node.type === TYPES.frame) {
          const frames = api.getFrames();
          const index = frames.indexOf(node);
          if (index === 0 && frames.length === 1) {
            return;
          }
        }

        function remove(id) {
          const children = api.getChildren(id);
          const nodes = ydoc.getMap<Y.Map<NodeLike>>('board').get('nodes')!;
          children.forEach((child) => remove(child.id)); // 修复：使用forEach而不是map
          nodes.delete(id);
          stageContext.providers.selection?.removeSelect(id); // 添加可选链
          stageContext.cache?.renderText?.remove(id);
          stageContext.cache?.renderImage?.remove(id);
        }
        this.ydoc.transact(() => {
          remove(id);
        });
        this.cache._nodes = null;
        this.cache._descendants = null; // 清除descendants缓存
        if (node.type === TYPES.frame) {
          stageContext.hooks.onFrameChange();
          api.layoutFrame();
        }
        stageContext.hooks.onLayerChange?.();
      },
      getAncenstors: (id: string) => {
        const node = stageContext.api.getNode(id);
        return getAncenstors(stageContext, node);
      },
      getFrameOf: (id: string) => {
        const node = api.getNode(id);
        if (!node) return null;
        const ancenstors = getAncenstors(stageContext, node);
        return ancenstors.find((node) => node.type === TYPES.frame) || null;
      },
      getChildren: (id: string | null) => {
        const nodes = stageContext.api.getNodes();
        return nodes
          .filter((n) => (id === null ? !n.parentId : n.parentId === id))
          .sort(sortByIndex);
      },
      getDescendants: (id: string) => {
        // 检查缓存
        if (!this.cache._descendants) {
          this.cache._descendants = new Map();
        }

        if (this.cache._descendants.has(id)) {
          return this.cache._descendants.get(id) as NodeType[];
        }

        const descendants: NodeType[] = [];
        const collectDescendants = (nodeId) => {
          const children = stageContext.api.getChildren(nodeId);
          children.forEach((child) => {
            descendants.push(child);
            collectDescendants(child.id);
          });
        };
        collectDescendants(id);

        // 缓存结果
        this.cache._descendants.set(id, descendants);
        return descendants;
      },
      updateNodeText: (id: string, patch) => {
        const text = patch.text;
        const node = api.getNode(id);
        if (!node || node.type !== TYPES.text || api.isNodeLocked(node)) return;

        const formerText = node.text || ''; // 添加默认值
        const former = wrapText(node, formerText);
        const formerLines = former.text;
        const newLines = wrapText(node, text).text;
        const diff = diffText(formerLines, newLines);
        const updatedHighlights = updateHighlightByText(node.highlights || [], diff); // 添加默认值
        const styles = updateHighlightByText(node.styles || [], diff); // 添加默认值

        api.transact(() => {
          api.updateNode(id, {
            highlights: updatedHighlights,
            styles: styles,
            text: text,
          });
        });
      },
      updateNode: (id, patch, { force = false } = {}) => {
        const node = api.getNode(id);
        if (!node) {
          throw new Error(`Node not found: ${id}`);
        }

        if (!force && !('locked' in patch) && api.isNodeLocked(node)) return;

        round(patch, 'x');
        round(patch, 'y');
        round(patch, 'w');
        round(patch, 'h');

        if (patch.meta && node.meta) {
          patch.meta = {
            ...node.meta,
            ...patch.meta,
          };
        }

        if ('parentId' in patch) {
          // Validate parent ID change to prevent invalid hierarchy
          const newParentId = patch.parentId;
          const newParent = api.getNode(newParentId as string);
          if (newParent && api.isNodeLocked(newParent)) return;

          // Check if trying to make node its own parent
          if (newParentId === id) {
            throw new Error(`Node cannot be its own parent: ${id}`);
          }

          // Check if trying to make node a child of its descendant
          if (newParentId) {
            const descendants = stageContext.api.getDescendants(id);
            if (descendants.some((desc) => desc.id === newParentId)) {
              throw new Error(`Cannot set a descendant as parent: ${newParentId}`);
            }
          }

          // 清除descendants缓存，因为层级关系发生了变化
          this.cache._descendants = null;
        }

        if (patch.parentId || patch.index) {
          stageContext.hooks.onLayerChange?.();
        }

        // 排除 patch 中的下划线开始的 key
        Object.keys(patch).forEach((key) => {
          if (key.startsWith('_')) {
            delete patch[key];
          }
        });

        if (stageContext.debug) {
          console.info('[state.updateNode]', id, patch);
        }

        const ydoc = this.ydoc;
        const changedKeys = Object.keys(patch);
        ydoc.transact(() => {
          const board = ydoc.getMap('board');
          const nodeList = board.get('nodes') as Y.Map<Y.Map<NodeType>>;
          const nodeMap = nodeList.get(id);

          if (nodeMap) {
            // 添加空值检查
            changedKeys.forEach((key) => {
              nodeMap.set(key, patch[key]);
              if (stageContext.debug) {
                console.info('[state.updateNode.set]', key, patch[key], nodeMap);
              }
            });
            api.markNodeUpdate(node, changedKeys);
          }
        });

        if (node.type === TYPES.frame) {
          if (typeof patch.w !== 'undefined' || typeof patch.h !== 'undefined') {
            api.layoutFrame();
          }
        }

        if (stageContext.providers.text && stageContext.providers.text.isEditing(node)) {
          stageContext.providers.text.update();
        }

        if ('fontFamily' in patch) {
          stageContext.api.loadFontsInUsed?.call();
        }

        // FIXME better type
        // @ts-expect-error allow clip
        if (patch.clip !== undefined || patch.x !== undefined || patch.y !== undefined) {
          this.nodeMoved$.set(Math.random());
        }
      },
      markNodeUpdate: (node: NodeType, changedKeys?: string[]) => {
        if (!node) return;

        stageContext.refreshTrigger.set({
          type: 'node',
          node,
        });

        // 使用 DirtyProvider 进行细粒度脏标记
        stageContext.providers.dirty.markDirty(node.id, changedKeys);

        if (node.type === TYPES.text) {
          layoutTextNode(node);
        }
        this.lastUpdatedNode$.set(node);
        api.markNeedRender();
      },
      markNeedRender: () => {
        stageContext.renderIndex = (stageContext.renderIndex || 0) + 1;
        if (stageContext.renderIndex > 1000) {
          stageContext.renderIndex = 0;
        }
        // trigger on-demand paint when scheduler is available
        stageContext.api.requestPaint();
      },
      isNodeLocked: (node: NodeType) => {
        if (!node) return true;
        if (node.locked) return true;
        const ancenstors = api.getAncenstors(node.id);
        if (ancenstors.find((n) => n.locked)) return true;
        return false;
      },
      getNodeBox: (node?: NodeType | null) => {
        if (!node) return null;
        if (!node.parentId) return Rect.from(node);
        const parent = stageContext.api.getNode(node.parentId);
        if (!parent) return Rect.from(node); // 添加父节点空值检查
        const parentBox = stageContext.api.getNodeBox(parent);
        if (!parentBox) return Rect.from(node); // 添加父节点box空值检查
        const rect = Rect.from(node);
        rect.x += parentBox.x || 0;
        rect.y += parentBox.y || 0;
        return rect;
      },
      getNodeRegion: (node: NodeType) => {
        return getNodeRegion(stageContext, node);
      },
      getSelectedNodesRegion: () => {
        const nodes = stageContext.api.getSelectedNodes();
        const region = api.getNodesRegion(nodes);
        region.nodes = nodes;
        return region;
      },
      getNodesRegion: (nodes) => {
        if (!nodes || !nodes.length) return Rect.zero; // 添加空值检查
        const rect = stageContext.api.getNodeRegion(nodes[0])?.rect;

        if (!rect) return Rect.zero; // 添加空值检查

        for (const node of nodes) {
          const nodeRegion = stageContext.api.getNodeRegion(node);
          if (nodeRegion && nodeRegion.rect) {
            // 添加空值检查
            rect.expandRect(nodeRegion.rect);
          }
        }
        return rect;
      },
      addFrame: (node) => {
        return api.addNode({
          type: TYPES.frame,
          name: 'Page',
          x: 0,
          y: 0,
          w: 1024,
          h: 768,
          ...node,
        });
      },
      getFrames: () => {
        return api
          .getChildren(null)
          .filter((node) => node.type === TYPES.frame)
          .sort(sortByIndex);
      },
      layoutFrame: () => {
        const padding = 160;
        const direction =
          stageContext.api.getClientConfig('layoutMode') || FRAME_LAYOUT_MODES.vertical;
        layoutFrame(stageContext, padding, direction);
        stageContext.hooks.onFrameChange();
      },
      setLayoutMode: (mode) => {
        api.setClientConfig('layoutMode', mode);
      },
      setClientConfig: (key, value) => {
        if (!stageContext.clientConfig) {
          stageContext.clientConfig = {};
        }
        stageContext.clientConfig[key] = value;
        // Trigger hooks if needed
        if (stageContext.hooks.onClientConfigChange)
          stageContext.hooks.onClientConfigChange(stageContext.clientConfig);
      },
      getClientConfig: (key, defaultValue = null) => {
        if (!stageContext.clientConfig) {
          return defaultValue;
        }
        return stageContext.clientConfig[key] !== undefined
          ? stageContext.clientConfig[key]
          : defaultValue;
      },
      getAllClientConfig: () => {
        return stageContext.clientConfig || {};
      },
      readClientConfig: () => {
        if (stageContext.hooks.onReadClientConfigChange) {
          const clientConfig = stageContext.hooks.onReadClientConfigChange();
          stageContext.clientConfig = clientConfig || {};
        }
      },
      clearClientConfig: () => {
        stageContext.clientConfig = {};
        if (stageContext.hooks.onClientConfigChange) stageContext.hooks.onClientConfigChange({});
      },
      getNodeName: getNodeName,
      export: () => this.state,
      import: (state) => this.setState(state),
    };
    Object.assign(stageContext.api, api);
  }

  setState(state) {
    this.state = JSON.parse(
      JSON.stringify(
        state || {
          nodes: {},
        }
      ),
      (key, val) => {
        if (key.startsWith('_')) {
          return null;
        } else {
          return val;
        }
      }
    );
    this.cache = {};
    const ydoc = new Y.Doc();
    this.ydoc = ydoc;
    docToYMap(this.state, ydoc);
    const undoManager = new Y.UndoManager(ydoc.getMap('board'));
    this.undoManager = undoManager;
    const context = this.stageContext;
    this.ydoc.on('update', () => {
      context.refreshTrigger.set({
        type: 'state',
      });
      context.hooks.onStateChange();
      context.api.markNeedRender();
    });

    const markNodeUpdate = (node) => {
      if (this.stageContext) this.stageContext.api.markNodeUpdate(node);
    };

    this.ydoc.getMap('board').observeDeep((events, _transaction) => {
      events.forEach((event) => {
        const map = event.target;
        const id = event.path[1];
        const memMap = this.state.nodes;
        const obj =
          event.path.length === 0 ? this.state : event.path.length === 1 ? memMap : memMap[id];
        const isNode = event.path.length === 2 && event.path[0] === 'nodes';

        event.keys.forEach((value, key) => {
          if (value.action === 'delete' || value.action === 'add') {
            this.cache._nodes = null;
            this.cache._descendants = null; // 清除descendants缓存
          }

          switch (value.action) {
            case 'update': {
              const value = map.get(key);
              obj[key] = value?.toJSON ? value.toJSON() : value;
              if (isNode && obj) markNodeUpdate(obj); // 修复：移除多余的key参数
              break;
            }
            case 'delete':
              delete obj[key];
              break;
            case 'add': {
              const n = map.get(key);
              obj[key] = n?.toJSON ? n.toJSON() : n;
              if (isNode && obj) markNodeUpdate(obj); // 修复：移除多余的key参数
              break;
            }
            default:
          }
        });
      });
    });
  }

  dispose() {
    // 添加清理逻辑
    if (this.ydoc) {
      this.ydoc.destroy();
    }
    if (this.undoManager) {
      this.undoManager.destroy();
    }
    this.disposed = true;
    this.state = {
      nodes: {},
    };
    this.cache = {};
  }
}

function getNodeName(name = 'Node', nodes) {
  const list = nodes || []; // 添加默认值
  const existNameNode = list.find((node) => node.name === name);
  if (!existNameNode) return name;
  const existNames = list.map((node) => node.name);
  let newName = name;

  let basename = name;

  if (basename.match(/\s\d+$/)) {
    // 修复正则表达式：使用\s而不是空格
    basename = basename.replace(/\s\d+$/, ''); // 修复正则表达式
  }

  let i = 1;
  while (existNames.includes(newName)) {
    newName = `${basename} ${i}`;
    i++;
    // 添加防止无限循环的保护
    if (i > 1000) {
      console.warn('getNodeName: Too many iterations, using timestamp');
      newName = `${basename} ${Date.now()}`;
      break;
    }
  }
  return newName;
}

function docToYMap(doc, ydoc) {
  const map = ydoc.getMap('board');
  const nodesMap = new Y.Map();
  map.set('nodes', nodesMap);
  createSyncNode(doc.background || {}, map, 'background');
  map.set('w', doc.w || 1024);
  map.set('h', doc.h || 1024);
  Object.values(doc.nodes || {}).forEach((node) => {
    createSyncNode(node, nodesMap);
  });
  return ydoc;
}

function createSyncNode(node, nodesMap, key?: string) {
  const nodeMap = new Y.Map();
  Object.keys(node).forEach((key) => {
    if (key.startsWith('_')) return;
    nodeMap.set(key, node[key]);
  });
  nodesMap.set(key || node.id, nodeMap);
}

function layoutFrame(store, padding = 120, direction = FRAME_LAYOUT_MODES.vertical) {
  const frames = store.api.getFrames();
  if (!frames || frames.length === 0) return; // 添加空值检查

  let currentX = 0;
  let currentY = 0;

  frames.forEach((frame) => {
    if (!frame) return; // 添加空值检查

    if (direction === FRAME_LAYOUT_MODES.vertical) {
      store.api.updateNode(frame.id, { x: 0, y: currentY }, { force: true });
      currentY += padding + (frame.h || 0); // 添加默认值
    } else {
      store.api.updateNode(frame.id, { x: currentX, y: 0 }, { force: true });
      currentX += padding + (frame.w || 0); // 添加默认值
    }
  });
}

function getAncenstors(stageContext: RenderContext, node?: NodeType | null) {
  const ancenstors: NodeType[] = [];
  if (!node) return ancenstors;
  if (!node.parentId) return ancenstors;
  if (!stageContext || !stageContext.api) return ancenstors; // 添加空值检查

  let current = node;
  let MAX_COUNT = 100;
  while (current && current.parentId) {
    const frame = stageContext.api.getNode(current.parentId);
    if (!frame) break;
    ancenstors.push(frame);
    current = frame;
    MAX_COUNT--;
    if (MAX_COUNT < 0) {
      console.error('getAncenstors: MAX_COUNT exceeded', node, ancenstors);
      break;
    }
  }
  return ancenstors;
}

function round(patch, key) {
  if (patch[key]) patch[key] = Math.round(patch[key]);
}

function getNodeRegion(stageContext: RenderContext, node: NodeType) {
  const box = stageContext.api.getNodeBox(node);
  if (!box) {
    return null;
  }

  let region = Region.fromRect(box);

  // 应用节点的变换矩阵
  const matrix = getNodeMatrix(node);
  if (matrix) {
    region = region.transformMatrix(matrix);
  }

  let currentNode = node;
  let currentRegion = region;
  let MAX_COUNT = 100; // 防止无限循环

  // 递归应用父节点的变换
  while (currentNode?.parentId && MAX_COUNT-- > 0) {
    const parentNode = stageContext.api.getNode(currentNode.parentId);
    if (!parentNode) break;

    const parentBox = stageContext.api.getNodeBox(parentNode);
    if (!parentBox) break;

    const parentMatrix = getNodeMatrix(parentNode);

    if (parentMatrix) {
      const parentRegion = Region.fromRect(parentBox);
      if (!parentRegion) break;

      const pCenter = parentRegion.center;
      currentRegion = currentRegion.transformMatrixByPoint(pCenter.x, pCenter.y, parentMatrix);
    }

    currentNode = parentNode;
  }

  return currentRegion;
}

declare module '../render/context/createRenderContext' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface RendererAPI extends StateAPI {}
}
